/**
 * Push Notification Service for Frontend
 * Handles browser push notification subscription and management
 * with improved PWA support and cross-platform compatibility
 */

import api from './api';

class PushNotificationService {
  constructor() {
    this.vapidPublicKey = null;
    this.subscription = null;
    this.initialized = false;
    this.serverEnabled = false;
  }

  /**
   * Check if push notifications are supported
   * Handles PWA and different browser contexts
   */
  isSupported() {
    // Basic support check
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;
    
    if (!hasServiceWorker || !hasPushManager || !hasNotification) {
      console.log('[Push] Not supported - missing APIs:', {
        serviceWorker: hasServiceWorker,
        pushManager: hasPushManager,
        notification: hasNotification
      });
      return false;
    }
    
    return true;
  }

  /**
   * Check if running as installed PWA
   */
  isPWA() {
    // Check various PWA indicators
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSPWA = window.navigator.standalone === true;
    const isAndroidTWA = document.referrer.includes('android-app://');
    
    return isStandalone || isIOSPWA || isAndroidTWA;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission; // 'granted', 'denied', or 'default'
  }

  /**
   * Initialize the service by fetching VAPID key
   * @returns {Promise<boolean>} Whether initialization was successful
   */
  async initialize() {
    if (this.initialized && this.vapidPublicKey) return true;
    
    if (!this.isSupported()) {
      console.warn('[Push] Push notifications not supported');
      return false;
    }

    try {
      const response = await api.get('/push/vapid-public-key');
      if (response.data.enabled && response.data.publicKey) {
        this.vapidPublicKey = response.data.publicKey;
        this.serverEnabled = true;
        this.initialized = true;
        console.log('[Push] Service initialized successfully');
        return true;
      }
      console.log('[Push] Server has push disabled');
      this.serverEnabled = false;
      return false;
    } catch (error) {
      console.error('[Push] Failed to get VAPID key:', error);
      this.serverEnabled = false;
      return false;
    }
  }

  /**
   * Request notification permission from user
   * @returns {Promise<boolean>} Whether permission was granted
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    // If already granted, return true
    if (Notification.permission === 'granted') {
      return true;
    }

    // If denied, we can't ask again
    if (Notification.permission === 'denied') {
      throw new Error('Notification permission denied');
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('[Push] Permission request failed:', error);
      throw new Error('Failed to request notification permission');
    }
  }

  /**
   * Wait for service worker to be ready
   * @param {number} timeout - Maximum wait time in ms
   * @returns {Promise<ServiceWorkerRegistration>}
   */
  async waitForServiceWorker(timeout = 10000) {
    const startTime = Date.now();
    
    while (Date.now() - startTime < timeout) {
      try {
        const registration = await navigator.serviceWorker.ready;
        if (registration.active) {
          return registration;
        }
      } catch (e) {
        // Service worker not ready yet
      }
      
      // Wait a bit before trying again
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    throw new Error('Service worker not ready');
  }

  /**
   * Subscribe to push notifications
   * @returns {Promise<boolean>}
   */
  async subscribe() {
    // Ensure initialized
    if (!await this.initialize()) {
      throw new Error('Push notifications not available');
    }

    // Request permission if not granted
    if (Notification.permission !== 'granted') {
      const granted = await this.requestPermission();
      if (!granted) {
        throw new Error('Notification permission denied');
      }
    }

    try {
      // Wait for service worker to be ready
      console.log('[Push] Waiting for service worker...');
      const registration = await this.waitForServiceWorker();
      console.log('[Push] Service worker ready');
      
      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();
      console.log('[Push] Existing subscription:', !!subscription);
      
      if (!subscription) {
        // Create new subscription
        console.log('[Push] Creating new subscription...');
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
        });
        console.log('[Push] New subscription created');
      }

      // Send subscription to server
      console.log('[Push] Registering subscription with server...');
      const response = await api.post('/push/subscribe', {
        subscription: subscription.toJSON()
      });
      
      if (!response.data.success) {
        throw new Error('Server failed to save subscription');
      }
      
      console.log('[Push] Subscription registered successfully');
      this.subscription = subscription;
      return true;
    } catch (error) {
      console.error('[Push] Subscription failed:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   * @returns {Promise<boolean>}
   */
  async unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();
        
        // Remove from server
        await api.post('/push/unsubscribe', {
          endpoint: subscription.endpoint
        });
      }

      this.subscription = null;
      return true;
    } catch (error) {
      console.error('[Push] Unsubscribe failed:', error);
      throw error;
    }
  }

  /**
   * Check if currently subscribed (both browser and server)
   * @returns {Promise<{browser: boolean, server: boolean}>}
   */
  async getSubscriptionStatus() {
    const result = {
      browser: false,
      server: false,
      synced: false
    };
    
    if (!this.isSupported()) return result;
    
    try {
      // Check browser subscription
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      result.browser = !!subscription;
      
      // Check server status
      try {
        const serverStatus = await api.get('/push/status');
        result.server = serverStatus.data.subscribed;
      } catch (e) {
        console.warn('[Push] Failed to check server status:', e);
      }
      
      result.synced = result.browser === result.server;
      
      return result;
    } catch (error) {
      console.error('[Push] Failed to check subscription status:', error);
      return result;
    }
  }

  /**
   * Check if currently subscribed (simple boolean for UI)
   * @returns {Promise<boolean>}
   */
  async isSubscribed() {
    if (!this.isSupported()) return false;
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch {
      return false;
    }
  }

  /**
   * Re-sync existing browser subscription with server
   * Call this on app load to ensure server knows about existing subscriptions
   * @returns {Promise<boolean>}
   */
  async syncSubscription() {
    if (!this.isSupported()) return false;
    
    try {
      // Check if browser has an existing subscription
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription && Notification.permission === 'granted') {
        // Re-register this subscription with the server
        const response = await api.post('/push/subscribe', {
          subscription: subscription.toJSON()
        });
        console.log('[Push] Existing subscription synced with server');
        return response.data.success;
      }
      return false;
    } catch (error) {
      console.error('[Push] Failed to sync subscription:', error);
      return false;
    }
  }

  /**
   * Get subscription status from server
   * @returns {Promise<{enabled: boolean, subscribed: boolean, subscriptionCount: number}>}
   */
  async getStatus() {
    try {
      const response = await api.get('/push/status');
      return response.data;
    } catch (error) {
      console.error('[Push] Failed to get status:', error);
      return { enabled: false, subscribed: false, subscriptionCount: 0 };
    }
  }

  /**
   * Send a test notification (generic)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async sendTest() {
    try {
      const response = await api.post('/push/test');
      return response.data;
    } catch (error) {
      console.error('[Push] Test notification failed:', error);
      throw error;
    }
  }

  /**
   * Send a specific type of test notification
   * @param {string} type - Type of notification to test
   * Valid types: 'bill-reminder', 'overdue', 'payment', 'household-invite', 'weekly-summary', 'autopay', 'security'
   * @returns {Promise<{success: boolean, type: string, message: string}>}
   */
  async sendTestByType(type) {
    try {
      const response = await api.post(`/push/test/${type}`);
      return response.data;
    } catch (error) {
      console.error(`[Push] Test ${type} notification failed:`, error);
      throw error;
    }
  }

  /**
   * Get available notification test types
   * @returns {Array<{value: string, label: string, description: string, icon: string}>}
   */
  getTestTypes() {
    return [
      { value: 'default', label: 'Test Notification', description: 'Basic test notification', icon: '🔔' },
      { value: 'bill-reminder', label: 'Bill Reminder', description: 'Upcoming bill due tomorrow', icon: '📅' },
      { value: 'overdue', label: 'Overdue Alert', description: 'Bill is past due', icon: '🚨' },
      { value: 'payment', label: 'Payment Confirmation', description: 'Payment recorded', icon: '✅' },
      { value: 'household-invite', label: 'Household Invitation', description: 'Invited to join household', icon: '🏠' },
      { value: 'weekly-summary', label: 'Weekly Summary', description: 'Weekly bill summary', icon: '📊' },
      { value: 'autopay', label: 'Auto-Pay Processed', description: 'Auto-pay recorded payment', icon: '🔄' },
      { value: 'security', label: 'Security Alert', description: 'New login detected', icon: '🔐' }
    ];
  }

  /**
   * Show a local notification (for testing or fallback)
   * @param {string} title - Notification title
   * @param {Object} options - Notification options
   * @returns {Promise<void>}
   */
  async showLocalNotification(title, options = {}) {
    if (!this.isSupported()) {
      throw new Error('Notifications not supported');
    }
    
    if (Notification.permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }
    
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/badge-72x72.png',
        ...options
      });
    } catch (error) {
      // Fallback to regular Notification API
      new Notification(title, options);
    }
  }

  /**
   * Convert VAPID key from base64 to Uint8Array
   * @param {string} base64String - Base64 encoded VAPID public key
   * @returns {Uint8Array}
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
